import { useState, useEffect } from 'react';
import { Grid, Stack, CircularProgress, Button } from '@mui/material';
import { useTheme } from '@material-ui/core/styles';
import { useQuery, useMutation } from 'react-query';
import { getConfig, addOrUpdateConfig, CONFIG, resetBalances } from 'src/services/config.service';
import { queryClient } from 'src';
import { useRecoilState } from 'recoil';
import { snackbarAtom } from 'src/atoms/snackbar';
import PulseLoader from 'src/components/loaders/PulseLoader';
import TuneRoundedIcon from '@material-ui/icons/TuneRounded';
import { FormControl, InputAdornment, InputLabel, OutlinedInput } from '@material-ui/core';
import ResetBalanceDialog from './ResetBalanceDialog';
const AdminConfigurations = () => {
  const theme = useTheme();
  const {
    data: config,
    isFetching,
    isLoading,
    isError,
    error
  } = useQuery('config', getConfig, {
    refetchOnWindowFocus: false
  });
  const [, setSnackbar] = useRecoilState(snackbarAtom);

  const [rate, setRate] = useState(null);
  const [percentageCovered, setPercentageCovered] = useState(null);
  const [ceilingSingle, setCeilingSingle] = useState(null);
  const [ceilingMarried, setCeilingMarried] = useState(null);
  const [ceilingOneChild, setCeilingOneChild] = useState(null);
  const [ceilingChildren, setCeilingChildren] = useState(null);

  const { mutate: resetBalancesMutate, isLoading: resetBalancesIsLoading } = useMutation(
    resetBalances,
    {
      onError: (error) => {
        setSnackbar({
          open: true,
          message: 'An error has occurred: ' + error.message,
          severity: 'error'
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries('config').then();
        setSnackbar({
          open: true,
          message: 'Balances have been reset successfully',
          severity: 'success'
        });
      }
    }
  );

  useEffect(() => {
    if (config) {
      setRate(config[CONFIG.USD_RATE]);
      setPercentageCovered(config[CONFIG.PERCENTAGE_COVERED]);
      setCeilingSingle(config[CONFIG.CEILING_SINGLE]);
      setCeilingMarried(config[CONFIG.CEILING_MARRIED]);
      setCeilingOneChild(config[CONFIG.CEILING_SINGLE_CHILD]);
      setCeilingChildren(config[CONFIG.CEILING_CHILDREN]);
    }
  }, [config]);

  const [showSave, setShowSave] = useState(false);
  const {
    mutate: mutateConfig,
    isLoading: editIsLoading,
    error: editError,
    isError: editIsError
  } = useMutation(addOrUpdateConfig, {
    onSuccess: () => {
      queryClient.invalidateQueries('config').then();
    }
  });

  const onSave = () => {
    mutateConfig({
      [CONFIG.USD_RATE]: rate || undefined,
      [CONFIG.PERCENTAGE_COVERED]: percentageCovered || undefined,
      [CONFIG.CEILING_SINGLE]: ceilingSingle || undefined,
      [CONFIG.CEILING_MARRIED]: ceilingMarried || undefined,
      [CONFIG.CEILING_SINGLE_CHILD]: ceilingOneChild || undefined,
      [CONFIG.CEILING_CHILDREN]: ceilingChildren || undefined
    });

    setShowSave(false);
  };

  useEffect(() => {
    if (editIsError || isError) {
      setSnackbar({
        open: true,
        message: 'An error has occurred: ' + (editError || error).message,
        severity: 'error'
      });
    }
  }, [config, isError, editIsError, setSnackbar, editError, error]);

  useEffect(() => {
    if (
      !isLoading &&
      !isFetching &&
      ((rate !== config[CONFIG?.USD_RATE] && rate !== '') ||
        (percentageCovered !== config[CONFIG?.PERCENTAGE_COVERED] && percentageCovered !== '') ||
        (ceilingSingle !== config[CONFIG?.CEILING_SINGLE] && ceilingSingle !== '') ||
        (ceilingMarried !== config[CONFIG?.CEILING_MARRIED] && ceilingMarried !== '') ||
        (ceilingOneChild !== config[CONFIG?.CEILING_SINGLE_CHILD] && ceilingOneChild !== '') ||
        (ceilingChildren !== config[CONFIG?.CEILING_CHILDREN] && ceilingChildren !== ''))
    ) {
      setShowSave(true);
    } else {
      setShowSave(false);
    }
  }, [
    rate,
    percentageCovered,
    ceilingSingle,
    ceilingMarried,
    ceilingOneChild,
    ceilingChildren,
    config,
    isLoading,
    isFetching
  ]);

  const containerStyle = {
    '& > *': {
      marginBottom: 2 + 'rem'
    }
  };

  const [confirmationOpen, setConfirmationOpen] = useState(false);

  if (isLoading)
    return (
      <Stack alignItems={'center'} justifyContent={'center'} height={'100%'}>
        <CircularProgress color="incorta" />
      </Stack>
    );
  if (isError)
    return (
      <Stack alignItems={'center'} justifyContent={'center'} height={'100%'}>
        {'An error has occurred: ' + error.message}
      </Stack>
    );
  return (
    <div style={{ ...containerStyle }}>
      <ResetBalanceDialog
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onSave={() => resetBalancesMutate()}
      />
      <Stack
        alignItems={'center'}
        direction="row"
        height={'100%'}
        justifyContent={'space-between'}
        style={{ marginBottom: '2rem' }}
      >
        <Stack direction="row" alignItems="center" style={{}}>
          <TuneRoundedIcon style={{ marginRight: '0.5rem', color: theme.palette.grey[700] }} />
          <h2 align="left">Configurations</h2>
        </Stack>
        {editIsLoading || resetBalancesIsLoading ? (
          <PulseLoader size={10} />
        ) : showSave && !editIsError ? (
          <Button color="incorta" onClick={() => onSave()}>
            Save Changes
          </Button>
        ) : showSave && editIsError ? (
          <Button color="incorta" disabled>
            Save Changes
          </Button>
        ) : (
          <Button
            style={{
              backgroundColor: theme.palette.grey[700],
              boxShadow: 'none'
            }}
            variant="contained"
            onClick={() => setConfirmationOpen(true)}
          >
            Reset Balances
          </Button>
        )}
      </Stack>
      <Grid container alignItems={'center'} spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel
              htmlFor="percentage-covered"
              style={{
                color:
                  Number(percentageCovered) < 0 || Number(percentageCovered) > 100 ? 'red' : 'grey'
              }}
            >
              Percentage Covered
            </InputLabel>
            <OutlinedInput
              label="Percentage Covered"
              id="percentage-covered"
              name={CONFIG.PERCENTAGE_COVERED}
              endAdornment={<InputAdornment position="start">%</InputAdornment>}
              aria-describedby="outlined-usd-helper-text"
              inputProps={{
                'aria-label': 'percentage-covered'
              }}
              onChange={(e) => {
                setPercentageCovered(Number(e.target.value));
              }}
              defaultValue={config[CONFIG.PERCENTAGE_COVERED]}
              type="number"
              aria-valuemax={100}
              aria-valuemin={0}
              error={Number(percentageCovered) < 0 || Number(percentageCovered) > 100}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel
              htmlFor="ceiling-single"
              style={{
                color: Number(ceilingSingle) < 0 ? 'red' : 'grey'
              }}
            >
              Ceiling | Single
            </InputLabel>
            <OutlinedInput
              label="Ceiling | Single"
              id="ceiling-single"
              name={CONFIG.CEILING_SINGLE}
              startAdornment={<InputAdornment position="start">$</InputAdornment>}
              aria-describedby="ceiling-single-helper-text"
              inputProps={{
                'aria-label': 'celing-single'
              }}
              onChange={(e) => {
                setCeilingSingle(Number(e.target.value));
              }}
              defaultValue={config[CONFIG.CEILING_SINGLE]}
              type="number"
              error={Number(ceilingSingle) < 0}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel
              htmlFor="ceiling-married"
              style={{
                color: Number(ceilingMarried) < 0 ? 'red' : 'grey'
              }}
            >
              Ceiling | Married
            </InputLabel>
            <OutlinedInput
              label="Ceiling | Married"
              id="ceiling-married"
              name={CONFIG.CEILING_MARRIED}
              startAdornment={<InputAdornment position="start">$</InputAdornment>}
              aria-describedby="ceiling-married-helper-text"
              inputProps={{
                'aria-label': 'celing-married'
              }}
              onChange={(e) => {
                setCeilingMarried(Number(e.target.value));
              }}
              defaultValue={config[CONFIG.CEILING_MARRIED]}
              type="number"
              error={Number(ceilingMarried) < 0}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel
              htmlFor="ceiling-one-child"
              style={{
                color: Number(ceilingOneChild) < 0 ? 'red' : 'grey'
              }}
            >
              Ceiling | One Child
            </InputLabel>
            <OutlinedInput
              label="Ceiling | One Child"
              id="ceiling-one-child"
              name={CONFIG.CEILING_SINGLE_CHILD}
              startAdornment={<InputAdornment position="start">$</InputAdornment>}
              aria-describedby="ceiling-one-child-helper-text"
              inputProps={{
                'aria-label': 'celing-one-child'
              }}
              onChange={(e) => {
                setCeilingOneChild(Number(e.target.value));
              }}
              defaultValue={config[CONFIG.CEILING_SINGLE_CHILD]}
              type="number"
              error={Number(ceilingOneChild) < 0}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel
              htmlFor="ceiling-children"
              style={{
                color: Number(ceilingChildren) < 0 ? 'red' : 'grey'
              }}
            >
              Ceiling | Children
            </InputLabel>
            <OutlinedInput
              label="Ceiling | Children"
              id="ceiling-children"
              name={CONFIG.CEILING_CHILDREN}
              startAdornment={<InputAdornment position="start">$</InputAdornment>}
              aria-describedby="ceiling-children-helper-text"
              inputProps={{
                'aria-label': 'celing-children'
              }}
              onChange={(e) => {
                setCeilingChildren(Number(e.target.value));
              }}
              defaultValue={config[CONFIG.CEILING_CHILDREN]}
              type="number"
              error={Number(ceilingChildren) < 0}
            />
          </FormControl>
        </Grid>
      </Grid>
    </div>
  );
};

export default AdminConfigurations;
